import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Text,
  Banner,
  List,
} from '@shopify/polaris';
import { useShop } from '../contexts/ShopContext';
import apiService from '../services/apiService';

function ShopConnect() {
  const [shopUrl, setShopUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [shopName, setShopName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { saveShop } = useShop();

  const validateShopUrl = (url) => {
    const cleanUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (cleanUrl.includes('.myshopify.com')) {
      return /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/.test(cleanUrl);
    }
    return /^[a-zA-Z0-9][a-zA-Z0-9-]*$/.test(cleanUrl);
  };

  const normalizeShopUrl = (url) => {
    let normalized = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!normalized.includes('.myshopify.com')) {
      normalized = `${normalized}.myshopify.com`;
    }
    return normalized;
  };

  const handleConnectApp = async () => {
    if (!validateShopUrl(shopUrl)) {
      setError('Please enter a valid Shopify store URL (e.g., your-store.myshopify.com)');
      return;
    }
    if (!clientId.trim() || !clientSecret.trim()) {
      setError('Client ID and Client Secret are required.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const normalizedShop = normalizeShopUrl(shopUrl);
      
      // Step 1: Exchange Client ID and Secret for an initial Access Token
      const res = await apiService.post('/api/shopify/ccg-token', {
        shop: normalizedShop,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim()
      });

      const { access_token, expires_in } = res.data;

      // Calculate expiry buffer (e.g., 5 minutes before actual expiry)
      const bufferMs = 5 * 60 * 1000;
      const tokenExpiresAt = Date.now() + (expires_in * 1000) - bufferMs;
      
      const scopes = [
        'read_price_rules','write_price_rules',
        'read_discounts_allocator_functions','write_discounts_allocator_functions',
        'read_files','write_files',
        'write_inventory','read_inventory',
        'write_locations','read_locations',
        'read_metaobject_definitions','write_metaobject_definitions',
        'read_metaobjects','write_metaobjects',
        'read_product_listings','write_product_listings',
        'read_products','write_products',
        'read_publications','write_publications',
        'read_content','write_content'
      ];

      const shopData = {
        name: shopName.trim() || normalizedShop.replace('.myshopify.com', ''),
        shop: normalizedShop,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        accessToken: access_token,
        tokenExpiresAt: tokenExpiresAt,
        tokenType: 'ccg_custom_app',
        scopes: scopes,
        installedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };

      // Step 2: Save everything directly to Firebase
      await saveShop(shopData);
      
      setSuccess(`Successfully connected to ${normalizedShop}! Your store is now fully linked to Editify.`);
      setShopUrl('');
      setClientId('');
      setClientSecret('');
      setShopName('');
    } catch (err) {
      console.error('Error saving shop connection:', err);
      // Check for specific API errors
      if (err.response && err.response.status === 401) {
        setError('Connection failed: Invalid Client ID or Client Secret.');
      } else {
        setError('Failed to securely connect store. Please check your details and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page title="Connect Your Shopify Store">
      <Layout>
        <Layout.Section>
          {success && (
            <Banner status="success" title="Success" onDismiss={() => setSuccess('')}>
              {success}
            </Banner>
          )}
          {error && (
            <Banner status="critical" title="Connection Error" onDismiss={() => setError('')}>
              {error}
            </Banner>
          )}
          
          <Card sectioned>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Text variant="headingLg" as="h2">
                Connect Editify Manually
              </Text>
              <Text variant="bodyMd" as="p" color="subdued">
                Enter your Store URL alongside the Client ID and Client Secret belonging to your Custom App. Editify will automatically fetch and rotate the required secure tokens.
              </Text>
            </div>
            
            <FormLayout>
              <TextField
                label="Shop URL"
                value={shopUrl}
                onChange={setShopUrl}
                placeholder="your-store.myshopify.com"
                helpText="E.g., your-store.myshopify.com"
              />
              
              <TextField
                label="Client ID (API Key)"
                value={clientId}
                onChange={setClientId}
                placeholder="Ex. 12abc345def68..."
                type="text"
                helpText="Found under API Credentials in your Custom App"
              />

              <TextField
                label="Client Secret"
                value={clientSecret}
                onChange={setClientSecret}
                placeholder="Ex. shpcs_abc123..."
                type="password"
                helpText="Found directly below your Client ID"
              />

              <TextField
                label="Shop Name (Optional)"
                value={shopName}
                onChange={setShopName}
                placeholder="My Store"
                helpText="A custom name to identify this store inside the Editify dashboard"
              />
              
              <div style={{ marginTop: '20px' }}>
                <Button
                  primary
                  size="large"
                  fullWidth
                  onClick={handleConnectApp}
                  loading={loading}
                  disabled={loading || !shopUrl || !clientId || !clientSecret}
                >
                  Connect Store
                </Button>
              </div>
            </FormLayout>
          </Card>

          <Card sectioned title="Step-by-Step: How to get your API Credentials">
            <List type="number">
              <List.Item>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Go to your Shopify Admin</Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Log into your store, click on <strong>Settings</strong> (bottom left) &rarr; <strong>Apps and sales channels</strong> &rarr; <strong>Develop apps</strong>.
                </Text>
              </List.Item>
              <List.Item>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Start from Dev Dashboard</Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Click on <strong>Start from Dev Dashboard</strong>. Name the app "Editify" (or copy it below).
                </Text>
                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                  <Button size="slim" onClick={() => navigator.clipboard.writeText('Editify')}>
                    Copy App Name: Editify
                  </Button>
                </div>
              </List.Item>
              <List.Item>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Configure Scopes & API Version</Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Paste the following Scopes into the text field and set the <strong>Webhooks API Version</strong> exactly to <strong>2025-10</strong>.
                </Text>
                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                  <Button 
                    size="slim" 
                    onClick={() => navigator.clipboard.writeText('read_price_rules,write_price_rules,read_discounts_allocator_functions,write_discounts_allocator_functions,read_files,write_files,write_inventory,read_inventory,write_locations,read_locations,read_metaobject_definitions,write_metaobject_definitions,read_metaobjects,write_metaobjects,read_product_listings,write_product_listings,read_products,write_products,read_publications,write_publications,read_content,write_content')}
                  >
                    Copy All Scopes
                  </Button>
                </div>
              </List.Item>
              <List.Item>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Release the App</Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Click <strong>Release</strong> (top right or bottom right). When prompted for a version name, enter <strong>1.0</strong> and click <strong>Release</strong> again.
                </Text>
              </List.Item>
              <List.Item>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Install the App</Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  In the left sidebar, click on your App name ("Editify"). Then click <strong>Install</strong> on the right side. Select your store if prompted and confirm the installation.
                </Text>
              </List.Item>
              <List.Item>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Copy API Credentials</Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Finally, click on <strong>Settings</strong> in the left sidebar. Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> displayed there and paste them into Editify above.
                </Text>
              </List.Item>
            </List>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default ShopConnect;
